import { MS_PER_DAY, TENANT_BILLING_CONFIGURATION_STATUS } from '@klareospace/pharos-lib-types';
import type { TenantBillingConfigurationView, TenantFreeTrial } from '@klareospace/pharos-lib-types';
import { isTrialRunning, resolveTrialPlannedEnd, toUtcStartOfDay } from '@klareospace/pharos-lib-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FreeTrialBannerState {
  /** Full remaining days after today, never negative — 0 on the trial's last visible day. */
  daysLeft: number;
  /** Snapshot of `trial.durationDays` — the total trial length to display as "out of Y". */
  durationDays: number;
  /** Whether the free-trial banner should render at all. */
  visible: boolean;
}

// ─── Util ─────────────────────────────────────────────────────────────────────

/** Pure visibility + day-count decision for the frontoffice free-trial banner, driven by the tenant's own trial and status. */
export function computeFreeTrialBannerState(
  view: TenantBillingConfigurationView | null | undefined,
  todayUtc: Date,
): FreeTrialBannerState {
  const trial = view?.configuration.trial;
  if (!trial) {
    return { daysLeft: 0, durationDays: 0, visible: false };
  }

  const isPending = view?.configuration.status === TENANT_BILLING_CONFIGURATION_STATUS.PENDING;
  const visible = hasTrialStarted(trial, todayUtc) && isTrialRunning(trial, todayUtc) && isPending;

  return {
    daysLeft: resolveDaysLeft(resolveTrialPlannedEnd(trial), todayUtc),
    durationDays: trial.durationDays,
    visible,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Whether a trial's planned start date has been reached — guards against a scheduled (future-dated) trial. */
export function hasTrialStarted(trial: TenantFreeTrial, todayUtc: Date): boolean {
  return todayUtc >= toUtcStartOfDay(trial.startedAt);
}

/** 0 on the trial's last calendar day, never negative — counts full remaining days after today. */
function resolveDaysLeft(plannedEnd: Date, todayUtc: Date): number {
  const diffDays = Math.round(
    (toUtcStartOfDay(plannedEnd).getTime() - toUtcStartOfDay(todayUtc).getTime()) / MS_PER_DAY,
  );
  return Math.max(0, diffDays - 1);
}
